import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { connect } from "react-redux";
import moment from "moment";
import DashboardDatePicker from "../DashboardDatePicker";
import ChartViewToggle from "../ChartViewToggle";
import ChartCard, { ChartTypeToggle, ResetButton } from "../ChartCard";
import useChartRequest from "../useChartRequest";
import { PALETTE, cartesianOptions, formatMoney, isAllZero, seriesDataset } from "../chartTheme";
import { sendPostRequest } from "../../../../../actions/app-actions";
import { getAdminBusinessId } from "../../../../../utils/authHelper";

const SalesChart = (props) => {
  const { rows, loading, refreshing, error, load } = useChartRequest(
    props.sendPostRequest,
    `/admin/event-sales-chart/${getAdminBusinessId()}`
  );
  const [chartType, setChartType] = useState("line");
  const [isAnnual, setIsAnnual] = useState(false);
  const [year, setYear] = useState(new Date());

  const request = (next = {}) => {
    const annual = next.isAnnual !== undefined ? next.isAnnual : isAnnual;
    const y = next.year !== undefined ? next.year : year;
    load({ is_month_view: annual ? "D" : "E", year: moment(y).format("YYYY") });
  };

  useEffect(() => {
    request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const values = rows.map((r) => Number(r.total) || 0);
  const total = values.reduce((a, b) => a + b, 0);
  const bestIndex = values.length ? values.indexOf(Math.max(...values)) : -1;
  const best = bestIndex >= 0 && values[bestIndex] > 0 ? rows[bestIndex] : null;

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.index),
      datasets: [
        seriesDataset({
          label: "Revenue",
          data: rows.map((r) => r.total),
          color: PALETTE.brand,
          type: chartType,
          fill: true,
        }),
      ],
    }),
    [rows, chartType]
  );

  const options = useMemo(() => cartesianOptions({ money: true }), []);

  const actions = (
    <>
      {!isAnnual && (
        <DashboardDatePicker
          mode="year"
          value={year}
          onChange={(val) => {
            const next = val || new Date();
            setYear(next);
            request({ year: next });
          }}
        />
      )}
      <ChartViewToggle
        leftLabel="Monthly"
        rightLabel="Yearly"
        isRightActive={isAnnual}
        onChange={(annual) => {
          setIsAnnual(annual);
          request({ isAnnual: annual });
        }}
      />
      <ChartTypeToggle value={chartType} onChange={setChartType} />
      <ResetButton
        onClick={() => {
          const now = new Date();
          setIsAnnual(false);
          setYear(now);
          request({ isAnnual: false, year: now });
        }}
      />
    </>
  );

  const summary =
    !loading && !error ? (
      <div className="dash-figure">
        <span className="dash-figure__value">{formatMoney(total)}</span>
        <span className="dash-figure__meta">
          {isAnnual ? "Last 5 years" : `Net revenue in ${moment(year).format("YYYY")}`}
          {best ? ` · Best: ${best.index} (${formatMoney(best.total)})` : ""}
        </span>
      </div>
    ) : null;

  return (
    <ChartCard
      title="Revenue"
      subtitle="Net sales after discounts, excluding cancellations"
      tooltipCode="P3F5"
      actions={actions}
      summary={summary}
      loading={loading}
      refreshing={refreshing}
      error={error}
      empty={isAllZero(values)}
      emptyTitle="No revenue in this period"
      height={260}
    >
      {chartType === "bar" ? (
        <Bar data={chartData} options={options} />
      ) : (
        <Line data={chartData} options={options} />
      )}
    </ChartCard>
  );
};

export default connect(null, { sendPostRequest })(SalesChart);
